/**
 * The update check, for a signed-in parent (the app shell mounts it). It asks
 * the server for its version when the shell mounts, each time Babia comes
 * back to the foreground, every 15 minutes while it is visible, and once more
 * to confirm a version seen once. lib/update.ts decides what an answer means.
 * The notice and Settings read the newer version from here, and reload
 * through `reload`, which first lets every write reach the server.
 */
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, unwrap } from "../api.ts";
import { useToast } from "../components/ui/toast.tsx";
import { track } from "./analytics.ts";
import {
  CHECK_EVERY_MS,
  CLIENT_VERSION,
  type AppState,
  confirmAt,
  decide,
  newerVersion,
  quietMoment,
  type Sighting,
} from "./update.ts";

/** What started a reload, for the `app updated` event. */
export type ReloadTrigger = "silent" | "notice" | "settings";

/** The reload in progress, read by the page it loads (sessionStorage). */
const RELOAD_KEY = "babia.reload";
/** The version this device ran last (localStorage). */
const VERSION_KEY = "babia.version";

export interface ReloadMark {
  from: string;
  to: string;
  /** Used up by the first page load after the reload; the loop guard keeps the rest. */
  trigger?: ReloadTrigger;
}

/**
 * What loaded this version, for `app updated`: the reload's trigger if the
 * mark left the version this device last ran, else "reopened" (Babia did not
 * reload, such as a cold start from the Home Screen).
 */
export function updatedBy(mark: ReloadMark | null, last: string): ReloadTrigger | "reopened" {
  return mark?.trigger && mark.from === last ? mark.trigger : "reopened";
}

function readMark(): ReloadMark | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    return raw ? (JSON.parse(raw) as ReloadMark) : null;
  } catch {
    return null;
  }
}

let noted = false;

/** Once per page load: record `app updated` if this device last ran another version. */
function noteVersion(): void {
  if (noted || !CLIENT_VERSION) return;
  noted = true;
  try {
    const last = localStorage.getItem(VERSION_KEY);
    const mark = readMark();
    if (last && last !== CLIENT_VERSION) {
      track("app updated", {
        from_version: last,
        to_version: CLIENT_VERSION,
        trigger: updatedBy(mark, last),
      });
    }
    localStorage.setItem(VERSION_KEY, CLIENT_VERSION);
    // The trigger was this page load's, so a later cold start is not credited to it.
    if (mark?.trigger)
      sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ from: mark.from, to: mark.to }));
  } catch {
    // Storage disabled: no event, and the update itself still works.
  }
}

/**
 * Resolves once no write is in flight (an entry, a removal, a setting): true
 * if every write it waited for was saved, false if one failed or if one is
 * still in flight after `timeoutMs`. It stops watching either way.
 */
export function writesSaved(queryClient: QueryClient, timeoutMs = Infinity): Promise<boolean> {
  const cache = queryClient.getMutationCache();
  const awaited = new Set(cache.findAll({ status: "pending" }));
  const saved = () => [...awaited].every((mutation) => mutation.state.status === "success");
  return new Promise((resolve) => {
    if (queryClient.isMutating() === 0) return resolve(saved());
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = cache.subscribe((event) => {
      if (event.type === "added") awaited.add(event.mutation);
      if (queryClient.isMutating() === 0) finish(saved());
    });
    function finish(result: boolean) {
      clearTimeout(timer);
      unsubscribe();
      resolve(result);
    }
    if (Number.isFinite(timeoutMs)) timer = setTimeout(() => finish(false), timeoutMs);
  });
}

/** How long a reload waits for writes before it gives up and keeps the page. */
export const FLUSH_TIMEOUT_MS = 15_000;

/**
 * Saves everything before a reload: closes the toast, which starts a removal
 * that still offers Undo, then waits for every write. It repeats until closing
 * the toast starts no write, so a removal made during the wait is saved too.
 * False if a write failed, or if one is still in flight after `timeoutMs`
 * (offline, say): the page stays, and the write with it.
 */
export async function flushWrites(
  queryClient: QueryClient,
  dismissToast: () => void,
  timeoutMs = FLUSH_TIMEOUT_MS,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    dismissToast();
    if (queryClient.isMutating() === 0) return true;
    const left = deadline - Date.now();
    if (left <= 0 || !(await writesSaved(queryClient, left))) return false;
  }
}

/** The sheets that hold unsent words, also while closed (the feedback draft). */
const drafts = new Set<string>();

/** A sheet that keeps unsent words when it closes calls this, so no reload by itself loses them. */
export function useDraftHeld(held: boolean): void {
  const id = useId();
  useEffect(() => {
    if (!held) return;
    drafts.add(id);
    return () => {
      drafts.delete(id);
    };
  }, [id, held]);
}

/** The screens where everything a parent types is in a sheet. */
const QUIET_SCREENS = new Set(["/", "/overview"]);

interface AppUpdate {
  /** A newer version that is live, once confirmed; null otherwise. */
  newer: string | null;
  /** Reload Babia once every write is saved. Works with no update too. */
  reload: (trigger: Exclude<ReloadTrigger, "silent">) => void;
  reloading: boolean;
}

const AppUpdateContext = createContext<AppUpdate>({
  newer: null,
  reload: () => window.location.reload(),
  reloading: false,
});

export function AppUpdateProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { dismiss, show } = useToast();
  const [newer, setNewer] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const started = useRef(false);

  /** Reload once every write is saved; `stillQuiet` checks again after the wait. */
  const reloadTo = useCallback(
    async (trigger: ReloadTrigger, to: string, stillQuiet: () => boolean = () => true) => {
      if (started.current) return;
      started.current = true;
      setReloading(true);
      const saved = await flushWrites(queryClient, dismiss);
      // A failed write keeps the page, so its "Couldn't save" toast stays in
      // view. A reload by itself also gives way to a parent who began
      // something while it waited.
      if (!saved || !stillQuiet()) {
        started.current = false;
        setReloading(false);
        // A tap that seems to do nothing says why: the write is still on its way.
        if (trigger !== "silent" && queryClient.isMutating() > 0)
          show("Babia is still saving. Try again in a moment.");
        return;
      }
      try {
        const mark: ReloadMark = { from: CLIENT_VERSION, to, trigger };
        sessionStorage.setItem(RELOAD_KEY, JSON.stringify(mark));
      } catch {
        // Storage disabled: the reload still works, without its loop guard.
      }
      window.location.reload();
    },
    [dismiss, queryClient, show],
  );

  useEffect(noteVersion, []);

  useEffect(() => {
    // The dev server builds no version in: there is nothing to compare.
    if (!CLIENT_VERSION) return;
    let sighting: Sighting | null = null;
    let touched = false;
    let stopped = false;
    let confirmTimer: ReturnType<typeof setTimeout> | undefined;
    const visible = () => document.visibilityState === "visible";
    const appState = (returning: boolean): AppState => {
      const path = window.location.pathname.replace(/\/+$/, "") || "/";
      return {
        returning,
        touched,
        quietScreen: QUIET_SCREENS.has(path),
        sheetOpen: document.querySelector('[role="dialog"][aria-modal="true"]') !== null,
        writing: queryClient.isMutating() > 0,
        draft: drafts.size > 0,
        reloadedFor: readMark()?.to ?? null,
      };
    };

    async function check(returning: boolean) {
      let server: string;
      try {
        server = (await unwrap(api.health.get())).version;
      } catch {
        return; // Offline, or mid-deploy: the next check asks again.
      }
      if (stopped) return;
      const now = Date.now();
      const result = decide(sighting, { client: CLIENT_VERSION, server }, now, appState(returning));
      sighting = result.sighting;
      const ready = newerVersion(sighting);
      setNewer(ready);
      clearTimeout(confirmTimer);
      const at = confirmAt(sighting);
      // Hidden, it waits: coming back checks anyway.
      if (at !== null)
        confirmTimer = setTimeout(() => {
          if (visible()) void check(false);
        }, at - now);
      if (result.action === "reload" && ready)
        void reloadTo("silent", ready, () => quietMoment(appState(true)));
    }

    const onTouch = () => {
      touched = true;
    };
    const onVisibility = () => {
      if (!visible()) return;
      touched = false;
      void check(true);
    };
    const timer = setInterval(() => {
      if (visible()) void check(false);
    }, CHECK_EVERY_MS);
    document.addEventListener("pointerdown", onTouch, true);
    document.addEventListener("keydown", onTouch, true);
    document.addEventListener("visibilitychange", onVisibility);
    void check(false);
    return () => {
      stopped = true;
      clearTimeout(confirmTimer);
      clearInterval(timer);
      document.removeEventListener("pointerdown", onTouch, true);
      document.removeEventListener("keydown", onTouch, true);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [queryClient, reloadTo]);

  const value = useMemo<AppUpdate>(
    () => ({
      newer,
      reload: (trigger) => void reloadTo(trigger, newer ?? CLIENT_VERSION),
      reloading,
    }),
    [newer, reloadTo, reloading],
  );
  return <AppUpdateContext.Provider value={value}>{children}</AppUpdateContext.Provider>;
}

export function useAppUpdate(): AppUpdate {
  return useContext(AppUpdateContext);
}
